import React, { Children, useEffect, useState } from 'react';
import { DEBOUNCE_DELAY } from '../../constants/constants';
import UserName from '../common/user/UserName';
import PropertySearchbox from '../propertyReport/search-box/PropertyReportSearchBox';
import DatePicker from 'react-datepicker';
import styles from './propertyReport.module.css';
import 'react-datepicker/dist/react-datepicker.css';
import CalendarIcon from '../../images/calendar-icon.svg';
import LocationIcon from '../../images/icon-location.svg';
import TimezoneIcon from '../../images/icon-time-zone.svg';
import BuildingIcon from '../../images/icon-building.svg';
import BuildingSizeIcon from '../../images/icon-property-size.svg';
import BedIcon from '../../images/icon-bed.svg';
import BathtubIcon from '../../images/icon-bathtub.svg';
import NoPropertyImg from '../../images/noPropertyImg.svg';
import { useDispatch } from 'react-redux';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import { getStateLandmark, getUnits } from '../../redux/landmark/actions/landmark.action';
import { useSelector } from 'react-redux';
import Loader from '../common/loader/Loader';
import CustModal from '../common/custmodal/CustModal';
import AvailabilityPopup from './PropertyReportPopup';
import { hasPermission } from '../auth/login/Login';
import { getLocationTimeZone } from '../../constants/constants';

type TabTitle = 'Idle' | 'Available';
interface Location {
	id: string;
	city: string;
	locationName: string;
	managementCompany: string;
	propertyStatus: string;
	state: string;
}
export interface Availability {
	locker: number;
	endDate: string;
	openTime: string;
	closeTime: string;
	startDate: string;
	availabilityId: string;
}
interface UnitDetails {
	size: number;
	baths: string;
	price: number;
	rooms: string;
	unitId: string;
	unitName: string;
	locationId: string;
	availability: Availability[] | null;
	unitNickName: string;
	buildingName: string;
}
interface TabProps {
	title: TabTitle;
}
interface ListItemProps {
	icon: string;
	altText: string;
	text: string;
}
interface TileItemProps {
	label: string;
	children?: React.ReactNode;
}

const propertyReport = () => {
	const dispatch = useDispatch<AppDispatch>();
	const {
		loading,
		data: { allLocations },
		locationUnits,
	} = useSelector((state: IRootState) => state.landmarkReducer);

	const permissionLoading = useSelector((state: IRootState) => state?.permissionsReducer?.loading);
	const canReadReports = hasPermission("Property Report", "Report", "read");

	const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
	const [startDate, endDate] = dateRange;

	const [units, setUnits] = useState<[UnitDetails[] | null, UnitDetails[] | null]>([null, null]);
	const [idle, available] = units;

	const [searchText, setSearchText] = useState('');
	const [isClicked, setIsClicked] = useState(false);
	const [showAvailabilityModal, setShowAvailabilityModal] = useState<boolean>(false);
	const [modalContent, setModalContent] = useState<Availability[] | null>(null);
	const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
	const [tab, setTab] = useState<TabTitle>('Idle');

	const locationDetails = locationUnits?.data?.[0]
	const today = new Date();

	const Messages = {
		NO_UNITS_ASSIGNED: "It seems like no units have been assigned to the property yet. Don't worry, we're here to help you get started. To begin, please reach out to your administrator or manager to assign units to the property.",
		NO_UNITS_FOUND: `It seems like no units have been ${tab.toLocaleLowerCase()}.`,
		SELECT_LOCATION: "Please select a location to view the property details."
	}

	const unitsCount = {
		Idle: locationUnits?.unitsWithoutAvailabilityCount || 0,
		Available: locationUnits?.unitsWithAvailabilityCount || 0,
		Total: locationUnits?.unitsWithoutAvailabilityCount + locationUnits?.unitsWithAvailabilityCount || 0
	}

	const addressParts = [
		locationDetails?.streetNumber,
		locationDetails?.streetName,
		locationDetails?.city,
		locationDetails?.state,
		locationDetails?.zip
	];
	const locationAddress = addressParts.filter(Boolean).join(', ');

	const formatDate = (date: Date | string | null) => {
		const dt = new Date(`${date}`);
		const year = dt.getUTCFullYear();
		const month = ('0' + (dt.getUTCMonth() + 1)).slice(-2);
		const day = ('0' + dt.getUTCDate()).slice(-2);
		return `${year}-${month}-${day}`;
	}
	const firstDayOfMonth = () => new Date(today.getFullYear(), today.getMonth(), 1)
	const setDefaultDates = () =>
		setDateRange([firstDayOfMonth(), today]);
	const autoSelectEndDate = () => {
		if (startDate && !endDate) {
			setDateRange([startDate, startDate > today ? startDate : today])
		}
	}

	const segregateUnitDetails = (unitDetails: UnitDetails[]) => {
		return unitDetails?.reduce(
			([withAvailability, withoutAvailability]: Array<UnitDetails>[], detail) => {
				(Array.isArray(detail.availability) ? withAvailability : withoutAvailability).push(detail);
				return [withAvailability, withoutAvailability];
			},
			[[], []],
		);
	}

	const isTabIdle = tab == 'Idle'
	const mapState = () => isTabIdle ? idle : available
	const setDefaultTab = () => setTab('Idle')

	const openModal = () => setShowAvailabilityModal(true);
	const resetUnits = () => setUnits([null, null]);

	const displayMessageWrapper = !loading && allLocations?.length > 0 && (!selectedLocation || !mapState()?.length);
	const message = !selectedLocation ? Messages.SELECT_LOCATION : (!idle || !available ? Messages.NO_UNITS_ASSIGNED : Messages.NO_UNITS_FOUND);

	const fetchLocations = (searchText = '') => {
		setSelectedLocation(null);
		resetUnits();
		dispatch(getStateLandmark(searchText));
	}
	const fetchLocationUnits = () =>
		dispatch(
			getUnits({
				locationIds: [(selectedLocation as Location)?.id],
				startDateTime: formatDate(startDate),
				endDateTime: formatDate(endDate),
			}),
		);

	useEffect(() => {
		fetchLocations();
		setDefaultDates();
	}, []);

	useEffect(() => {
		if (selectedLocation && endDate) {
			fetchLocationUnits();
		}
	}, [selectedLocation, endDate]);

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (searchText.trim() !== '') {
				fetchLocations(searchText);
			} else {
				isClicked && fetchLocations();
			}
		}, DEBOUNCE_DELAY);
		return () => clearTimeout(delayDebounceFn);
	}, [searchText, dispatch, isClicked]);

	useEffect(() => {
		if (locationUnits) {
			const [withAvailability, withoutAvailability] = segregateUnitDetails(locationUnits?.data?.[0]?.unitDetails || []);
			setUnits([withoutAvailability, withAvailability]);
		}
	}, [locationUnits]);

	const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === ' ' && searchText && searchText.length === 0) {
			event.preventDefault();
		}
	};
	const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
		const { value } = e.target as HTMLInputElement;
		const trimmedValue = value.replace(/^\s+/, '');

		setSearchText(trimmedValue);
		setIsClicked(true);
	};
	const handleSelectLocation = (location: Location) => {
		setDefaultTab();
		resetUnits();
		setSelectedLocation(location)
	}
	const handleOpenModal = (unit: UnitDetails) => () => {
		openModal();
		const availability = unit?.availability;
		availability?.sort((a, b) =>
			new Date(b.startDate).getTime() - new Date(a.startDate).getTime() ||
			b.openTime.localeCompare(a.openTime)
		);
		setModalContent(availability);
	}

	const Tab = (tabProps: TabProps) => {
		const title = tabProps?.title;
		const counts = unitsCount[title];
		return (
			<li className={tab == title ? styles.active : ''} onClick={() => setTab(title)}>
				{title} ({counts})
			</li>
		);
	};
	const ListItem = ({ icon, altText, text }: ListItemProps) => {
		return (
			<li>
				<img src={icon} alt={altText} /> {text}
			</li>
		);
	}
	const TileItem = ({ label, children }: TileItemProps) => (
		<li>
			<div className={styles.propertyListInfoLabel}>{label}</div>
			{children}
		</li>
	);


	return (
		<div className={`outletConainer ${styles.propertyReportPG}`}>
			{(loading || permissionLoading) && <Loader />}
			{showAvailabilityModal && <CustModal
				open={showAvailabilityModal}
				setOpen={setShowAvailabilityModal}
				className="appointment-popup availability-popup"
				bodyData={
					<AvailabilityPopup
						setShowAvailabilityModal={setShowAvailabilityModal}
						showAvailabilityModal={showAvailabilityModal}
						modalContent={modalContent}
					/>
				}
			/>}

			<div className={styles.headingbar}>
				<div>
					<h1 className={styles.userGreet}>
						Welcome <UserName />
					</h1>
					<p className={styles.pageTitle}>Property Report</p>
				</div>
				{canReadReports && <div className={styles.reportFilter}>
					<div className={`${styles.datePickerWrapper} datePicker-wrapper`}>
						<DatePicker
							selected={startDate}
							dateFormat="MMM dd, yyyy"
							showPopperArrow={false}
							timeIntervals={30}
							placeholderText="Start Date - End Date"
							name="Start Date"
							className="custom-dateInput"
							isClearable={false}
							selectsRange={true}
							startDate={startDate}
							endDate={endDate}
							// maxDate={today}
							onChange={(e) => {
								setDateRange(e);
							}}
							onCalendarClose={autoSelectEndDate}
						/>
						<img src={CalendarIcon} alt="calendar" />
					</div>
				</div>}
			</div>

			{canReadReports && <div className={styles.propertyWrapper}>
				<div className={styles.propertySearchWrapper}>
					<PropertySearchbox
						searchText={searchText}
						onKeyPress={handleKeyPress}
						onChange={handleInputChange}
						placeholderText="Search for properties"
					/>

					<div className={`${styles.propertySearchList} customScrollbar`}>
						{!loading && !allLocations?.length && <div className={styles.noPropertyWrapper}>
							<p>No Properties Found</p>
						</div>}
						<ul>
							{allLocations?.map((location: Location) => (
								<li
									key={location.id}
									className={selectedLocation?.id === location.id ? styles.active : ''}
									onClick={() => handleSelectLocation(location)}
								>{location?.locationName}</li>
							))}
						</ul>
					</div>
				</div>
				<div className={`${styles.propertyDetailsWrapper} customScrollbar`}>
					{!loading && locationUnits && <> <div className={styles.propertyDetail}>
						<h2 className={styles.propertyTitleCode}>{locationDetails?.locationName} (#{locationDetails?.provisioningId})</h2>
						<ul>
							<ListItem icon={LocationIcon} altText="Location Icon" text={locationAddress} />
							<ListItem icon={TimezoneIcon} altText="Timezone Icon" text={getLocationTimeZone(locationDetails?.timeZone)} />
							<ListItem icon={BuildingIcon} altText="Building Icon" text={`${unitsCount['Total']} ${unitsCount['Total'] > 1 ? 'Units' : 'Unit'}`} />
						</ul>
					</div>
						<div className={styles.propertyStatusSelection}>
							<ul>
								<Tab title="Idle" />
								<Tab title="Available" />
							</ul>
						</div>
						<div className={styles.propertyDetailList}>
							{mapState()?.map((unit) => (
								<div key={`${unit?.locationId}-${unit?.unitId}`} className={`${styles.propertyDetailListBox} ${!isTabIdle ? styles.active : ''}`}>
									<h3 className={styles.propertyListTitle}>{`${unit.unitName} ${unit.unitNickName ? `"${unit.unitNickName}"` : ''}`}</h3>
									{unit?.buildingName && <p className={styles.buildingName}>Building: <span>{unit?.buildingName}</span></p>}
									<ul className={styles.propertyListInfoList}>
										<TileItem label="Availability">
											<div className={!isTabIdle ? styles.active : ''}>{`${isTabIdle ? 'is not' : ''} set`}</div>
										</TileItem>
										{/* <TileItem label="Start Date">
											<div>-</div>
										</TileItem>
										<TileItem label="End Date">
											<div>-</div>
										</TileItem>
										<TileItem label="Locker No.">
											<div>-</div>
										</TileItem> */}
									</ul>
									<ul className={styles.propertyListAmenitiesList}>
										<ListItem icon={BuildingSizeIcon} altText="Building Size Icon" text={`${unit.size ? unit.size : 0} Sq Ft`} />
										<ListItem icon={BedIcon} altText="Bed Icon" text={`${Number(unit?.rooms) !== 0 ? Number(unit?.rooms) : Number(unit?.rooms) === 0 ? '' : 0} ${Number(unit?.rooms) === 1 ? 'Bed' : Number(unit?.rooms) === 0 ? 'Studio' : 'Beds'}`} />
										<ListItem icon={BathtubIcon} altText="Bathtub Icon" text={`${+unit.baths} ${+unit?.baths === 1 ? 'Bath' : 'Baths'}`} />
									</ul>
									{!isTabIdle && <p className={styles.propertyViewLink} onClick={handleOpenModal(unit)}>View Details</p>}
								</div>))}
						</div>
					</>
					}

					{displayMessageWrapper && <div className={styles.noPropertyListWrapper}>
						<div className={styles.noPropertyListDetail}>
							<img src={NoPropertyImg} width={200} height={200} alt="Oops, Something went wrong!" />
							<p>
								{message}
							</p>
						</div>
					</div>}
				</div>
			</div>}
		</div >
	);
};

export default propertyReport;

function dispatch(arg0: any) {
	throw new Error('Function not implemented.');
}
